import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { invalidateCachedFetch, useAuth } from '@structural-climbing/auth'
import { Button, LanguageSwitcher } from '@structural-climbing/ui'
import { InstallAppButton } from '../features/pwa/InstallAppButton'
import { GymMark } from '../features/nav/GymMark'
import { useMyGyms, type MyGym } from '../features/gym/useMyGyms'
import { useMyProfile } from '../features/profile/useMyProfile'
import { memberFullName } from '../features/profile/displayName'
import { useViewedGym } from '../context/ViewedGymContext'
import { Page } from '../design/Page'
import { SurfaceCard } from '../design/SurfaceCard'
import { SkeletonCard } from '../design/Skeleton'

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <p className="text-sm text-foreground/50">{label}</p>
      <p className="min-w-0 truncate text-sm font-medium">{value}</p>
    </div>
  )
}

/**
 * The profile of a child's own login.
 *
 * Read-only on purpose. The parent decides the name, the date of birth and the
 * gender, from the Family section of their own account, and the server refuses
 * anything else. What is the child's to choose is which of their own gyms
 * they are looking at, and the language the app speaks.
 */
export function ChildProfilePage() {
  const { user, logout, getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const { profile, loading } = useMyProfile()
  const { gyms } = useMyGyms()
  const { gym: viewedGym } = useViewedGym()
  const activeGym = gyms.find((g) => g.slug === viewedGym?.slug) ?? gyms[0] ?? null

  // Remembered on the profile so the next sign-in lands on the same gym. Best
  // effort: the switch itself is only a navigation and must not wait on it.
  async function save(body: { preferredGymId: string } | { language: string }) {
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-profile`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res.ok) invalidateCachedFetch('my-profile')
    } catch {
      // Nothing to show: the choice still applies for this session.
    }
  }

  function switchTo(gym: MyGym) {
    void save({ preferredGymId: gym.id })
    void navigate(`/${gym.slug}/bouldering/routes`)
  }

  if (loading) {
    return (
      <Page>
        <SkeletonCard lines={3} />
        <SkeletonCard lines={2} />
      </Page>
    )
  }

  const notSet = t('childProfile.notSet')
  const gender =
    profile?.gender === 'male'
      ? t('profile.genderMale')
      : profile?.gender === 'female'
        ? t('profile.genderFemale')
        : notSet

  return (
    <Page>
      <h1 className="sr-only">{t('profile.title')}</h1>

      <SurfaceCard className="flex flex-col gap-4 p-6">
        <Detail label={t('childProfile.nameLabel')} value={memberFullName(profile, user, notSet)} />
        <Detail
          label={t('profile.dateOfBirthLabel')}
          value={profile?.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString() : notSet}
        />
        <Detail label={t('profile.genderLabel')} value={gender} />
        <p className="text-xs text-foreground/50">{t('childProfile.managedByParent')}</p>
      </SurfaceCard>

      {/* Only gyms where the child has an active plan are listed, so with none
          there is nothing to switch to and the reason is said out loud. */}
      {gyms.length === 0 && (
        <SurfaceCard className="p-6">
          <p className="text-sm text-foreground/60">{t('childProfile.noGym')}</p>
        </SurfaceCard>
      )}

      {gyms.length > 0 && (
        <SurfaceCard className="flex flex-col gap-2 p-6">
          <h2 className="eyebrow mb-1 text-foreground/40">{t('childProfile.gymTitle')}</h2>
          {gyms.map((gym) => {
            const isActive = gym.id === activeGym?.id
            return (
              <Button
                key={gym.id}
                variant={isActive ? 'flat' : 'light'}
                className="h-auto justify-start gap-3 px-3 py-2.5"
                aria-current={isActive ? 'true' : undefined}
                onClick={() => switchTo(gym)}
              >
                <GymMark name={gym.name} imageUrl={gym.image_url} size="md" />
                <span className="min-w-0 flex-1 truncate text-left text-sm font-medium">
                  {gym.name}
                </span>
                {isActive && (
                  <span aria-hidden className="text-sm text-[var(--gym-primary)]">
                    ✓
                  </span>
                )}
              </Button>
            )
          })}
        </SurfaceCard>
      )}

      <SurfaceCard className="flex flex-col gap-5 p-6">
        <div>
          <p className="mb-2 text-sm font-medium">{t('profile.languageLabel')}</p>
          <LanguageSwitcher onChange={(language) => void save({ language })} />
        </div>
        <InstallAppButton fullWidth />
      </SurfaceCard>

      <Button
        variant="flat"
        color="danger"
        onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
      >
        {t('common.signOut')}
      </Button>
    </Page>
  )
}
